import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import Button from "../../../components/Button/Button";
import Input from "../../../components/Input/Input";
import { sendLoginOtp, verifyLoginOtp } from "../../../services/StudentPortal/StudentAuthService";
import { handleApiError } from "../../../utils/errorHandler";
import { STUDENT_STORAGE_KEYS } from "../../../services/StudentPortal/studentStorageKeys";
import loginImg from "../../../assets/image/evalution-icon.png";
import "./StudentLogin.css";

// Confirmed against the real backend (2026-10-22): the OTP identifier is the
// student's admission/ID number ("id_number"), not a mobile number or email.
const LOGIN_FIELD_NAME = "id_number";

const StudentLogin = () => {
  const [step, setStep] = useState("identify"); // identify | otp
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Already signed in — don't let a logged-in student navigate back to the
  // login page (e.g. by URL or browser back button); only logging out or an
  // expired session (which clears studentToken) should land them here.
  if (localStorage.getItem(STUDENT_STORAGE_KEYS.TOKEN)) {
    return <Navigate to="/student-portal/dashboard" replace />;
  }

  const handleSendOtp = async (event) => {
    event.preventDefault();
    if (!identifier.trim()) {
      enqueueSnackbar("Enter your admission/ID number", { variant: "error" });
      return;
    }
    setLoading(true);
    try {
      await sendLoginOtp({ [LOGIN_FIELD_NAME]: identifier.trim() });
      enqueueSnackbar("OTP sent", { variant: "success" });
      setStep("otp");
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    if (!otp.trim()) {
      enqueueSnackbar("Enter the OTP", { variant: "error" });
      return;
    }
    setLoading(true);
    try {
      const response = await verifyLoginOtp({ [LOGIN_FIELD_NAME]: identifier.trim(), otp: otp.trim() });
      // Confirmed against the real backend (2026-10-22): the response is
      // { message, token, token_type, expires_at } — token is top-level,
      // not nested under `data`, and there's no student/user object here
      // (StudentSessionContext fetches the full profile via
      // getStudentDetails() right after this, using the token).
      const token = response?.data?.token;
      if (!token) {
        enqueueSnackbar("Login failed. Please try again.", { variant: "error" });
        return;
      }
      // Wipe any leftover staff session first — on a shared/school computer a
      // staff member's "token" can otherwise still be sitting in localStorage
      // after a student logs in here, and the staff app's ProtectedRoute only
      // checks for that token's presence, not who it belongs to, which would
      // let the student open staff/teacher pages with the old staff session.
      localStorage.removeItem("token");
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      localStorage.removeItem("organization_id");
      localStorage.removeItem("roles");
      localStorage.removeItem("permissions");
      localStorage.removeItem("session_id");

      localStorage.setItem(STUDENT_STORAGE_KEYS.TOKEN, token);
      navigate("/student-portal/dashboard");
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="student-login">
      <div className="student-login-card">
        <div className="student-login-brand">
          <span className="student-login-img-wrap">
            <img src={loginImg} alt="Student Portal" className="student-login-img" />
          </span>
          <h2 className="student-login-title">Student Portal</h2>
          <p className="student-login-subtitle">Sign in with your admission/ID number</p>
        </div>

        <div className="student-login-body">
          {step === "identify" ? (
            <form onSubmit={handleSendOtp} className="student-login-form">
              <Input
                label="Admission Number"
                name="identifier"
                value={identifier}
                onChangeHandler={(event) => setIdentifier(event.target.value)}
                placeholder="Enter your admission/ID number"
                required
              />
              <Button type="submit" block loading={loading} className="student-login-submit">
                Send OTP
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="student-login-form">
              <Input
                label="OTP"
                name="otp"
                value={otp}
                onChangeHandler={(event) => setOtp(event.target.value)}
                placeholder="Enter the OTP sent to your registered mobile"
                className="student-login-otp-input"
                required
              />
              <Button type="submit" block loading={loading} className="student-login-submit">
                Verify &amp; Continue
              </Button>
              <div className="student-login-change-wrap">
                <Button
                  type="button"
                  variant="link"
                  className="student-login-change-link"
                  onClick={() => setStep("identify")}
                >
                  Back
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentLogin;
